import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface Record {
  email: string;
  conclusion: string;
  timestamp: string;
  path: string;
  feedback: string;
}

interface SurveyRecord {
  timestamp: string;
  q1_identity: string;
  q2_struggle: string;
  q3_released: string;
  q4_belief: string;
  q5_missing: string;
  q6_wantFromJourney: string;
  q7_liveWebinar: string;
  q7_email: string;
}

interface WebinarRecord {
  name: string;
  email: string;
  phone: string;
  timestamp: string;
}

const AdminMagicData = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState('');
  const [fileSize, setFileSize] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [records, setRecords] = useState<Record[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  
  // Survey data state
  const [surveyRecordCount, setSurveyRecordCount] = useState<number>(0);
  const [surveyRecords, setSurveyRecords] = useState<SurveyRecord[]>([]);
  const [surveyFileSize, setSurveyFileSize] = useState<number>(0);
  const [currentSurveyPage, setCurrentSurveyPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'magic' | 'survey' | 'webinar'>('magic');

  // Webinar data state
  const [webinarRecordCount, setWebinarRecordCount] = useState<number>(0);
  const [webinarRecords, setWebinarRecords] = useState<WebinarRecord[]>([]);
  const [webinarFileSize, setWebinarFileSize] = useState<number>(0);
  const [currentWebinarPage, setCurrentWebinarPage] = useState(1);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Test authentication by making a request
      const response = await fetch('/api/admin-magic-data', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (response.ok) {
        setAuthenticated(true);
        await Promise.all([fetchBlobInfo(), fetchSurveyInfo(), fetchWebinarInfo()]);
      } else {
        const data = await response.json();
        setError(data.error || 'Invalid password');
      }
    } catch (error) {
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlobInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-magic-data', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFileSize(data.fileSize || 0);
          setRecordCount(data.recordCount || 0);
          setRecords(data.records || []);
        }
      } else if (response.status === 401) {
        setAuthenticated(false);
        setError('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Error fetching blob info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-survey-data', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSurveyFileSize(data.fileSize || 0);
          setSurveyRecordCount(data.recordCount || 0);
          setSurveyRecords(data.records || []);
        }
      } else if (response.status === 401) {
        setAuthenticated(false);
        setError('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Error fetching survey info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebinarInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-webinar-data', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWebinarFileSize(data.size || 0);
        setWebinarRecordCount(data.recordCount || 0);
        setWebinarRecords(data.records || []);
      } else if (response.status === 401) {
        setAuthenticated(false);
        setError('Session expired. Please login again.');
      }
    } catch (error) {
      console.error('Error fetching webinar info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-magic-data?download=true', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `magic-tool-submissions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyDownload = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-survey-data?download=true', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey-submissions-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading survey file:', error);
      alert('Failed to download survey file');
    } finally {
      setLoading(false);
    }
  };

  const handleWebinarDownload = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin-webinar-data?download=true', {
        headers: {
          'Authorization': `Bearer ${password}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `webinar-registrations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading webinar file:', error);
      alert('Failed to download webinar file');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const storageUsedPercentage = (fileSize / (500 * 1024 * 1024)) * 100;

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = records.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(records.length / recordsPerPage);

  // Survey pagination
  const indexOfLastSurveyRecord = currentSurveyPage * recordsPerPage;
  const indexOfFirstSurveyRecord = indexOfLastSurveyRecord - recordsPerPage;
  const currentSurveyRecords = surveyRecords.slice(indexOfFirstSurveyRecord, indexOfLastSurveyRecord);
  const totalSurveyPages = Math.ceil(surveyRecords.length / recordsPerPage);

  // Webinar pagination
  const indexOfLastWebinarRecord = currentWebinarPage * recordsPerPage;
  const indexOfFirstWebinarRecord = indexOfLastWebinarRecord - recordsPerPage;
  const currentWebinarRecords = webinarRecords.slice(indexOfFirstWebinarRecord, indexOfLastWebinarRecord);
  const totalWebinarPages = Math.ceil(webinarRecords.length / recordsPerPage);

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4">
        <Helmet>
          <title>Admin - Magic Tool Data</title>
        </Helmet>
        
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              🔒 Admin Access
            </h1>
            <p className="text-gray-600">
              Magic Tool Data Management
            </p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                className={`w-full px-4 py-3 border ${error ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all`}
                placeholder="Enter admin password"
                autoFocus
              />
              {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>

            <button
              type="submit"
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Login →
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
      <Helmet>
        <title>Admin Dashboard - Magic Tool Data</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              📊 Admin Dashboard
            </h1>
            <button
              onClick={() => {
                setAuthenticated(false);
                setPassword('');
              }}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Logout
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('magic')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'magic'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🔮 Magic Tool Data
            </button>
            <button
              onClick={() => setActiveTab('survey')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'survey'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📋 Community Pulse Survey
            </button>
            <button
              onClick={() => setActiveTab('webinar')}
              className={`px-6 py-3 font-semibold transition-all ${
                activeTab === 'webinar'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🎓 Webinar Registrations
            </button>
          </div>

          {/* Magic Tool Data */}
          {activeTab === 'magic' && (
            <>
              {/* Storage Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Storage Used</h3>
                    <span className="text-2xl">💾</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-2">
                    {formatBytes(fileSize)}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(storageUsedPercentage, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600">
                    {storageUsedPercentage.toFixed(2)}% of 500 MB
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Total Submissions</h3>
                    <span className="text-2xl">📝</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {recordCount}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Users who completed the diagnostic tree
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <button
                  onClick={handleDownload}
                  disabled={loading || recordCount === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>⬇️</span>
                      <span>Download CSV Data</span>
                    </>
                  )}
                </button>

                <button
                  onClick={fetchBlobInfo}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50"
                >
                  🔄 Refresh Data
                </button>
              </div>

              {recordCount === 0 && !loading && (
                <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ℹ️ No submissions yet. Data will appear here once users complete the Magic Tool.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Survey Data */}
          {activeTab === 'survey' && (
            <>
              {/* Storage Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Storage Used</h3>
                    <span className="text-2xl">💾</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900 mb-2">
                    {formatBytes(surveyFileSize)}
                  </p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">Total Survey Responses</h3>
                    <span className="text-2xl">📝</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {surveyRecordCount}
                  </p>
                  <p className="text-xs text-gray-600 mt-2">
                    Users who completed the community pulse survey
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <button
                  onClick={handleSurveyDownload}
                  disabled={loading || surveyRecordCount === 0}
                  className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>⬇️</span>
                      <span>Download Survey CSV</span>
                    </>
                  )}
                </button>

                <button
                  onClick={fetchSurveyInfo}
                  disabled={loading}
                  className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50"
                >
                  🔄 Refresh Data
                </button>
              </div>

              {surveyRecordCount === 0 && !loading && (
                <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ℹ️ No survey responses yet. Data will appear here once users complete the survey.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Magic Tool Data Table */}
        {activeTab === 'magic' && records.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              📋 Recent Submissions
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Conclusion</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Feedback</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Journey</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.map((record, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {indexOfFirstRecord + index + 1}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.email}
                      </td>
                      <td className="py-4 px-4 text-sm font-medium text-blue-600">
                        {record.conclusion}
                      </td>
                      <td className="py-4 px-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.feedback === 'Very Helpful' ? 'bg-green-100 text-green-800' :
                          record.feedback === 'Helpful' ? 'bg-blue-100 text-blue-800' :
                          record.feedback === 'Not Very Helpful' ? 'bg-orange-100 text-orange-800' :
                          record.feedback === 'Not Helpful' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {record.feedback}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(record.timestamp)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 hover:text-blue-800">
                            View Path →
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs">
                            {record.path.split(' -> ').map((step, i) => (
                              <div key={i} className="mb-2 last:mb-0">
                                <strong>Step {i + 1}:</strong> {step}
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, records.length)} of {records.length} entries
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    ← Previous
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          currentPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Survey Data Table */}
        {activeTab === 'survey' && surveyRecords.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              📋 Recent Survey Responses
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Identity</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Struggle</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Released</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Belief</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Missing</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Live Webinar</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSurveyRecords.map((record, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {indexOfFirstSurveyRecord + index + 1}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(record.timestamp)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.q1_identity}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.q2_struggle}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.q3_released}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.q4_belief}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">
                        {record.q5_missing}
                      </td>
                      <td className="py-4 px-4 text-sm">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.q7_liveWebinar === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {record.q7_liveWebinar}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 hover:text-blue-800">
                            View More →
                          </summary>
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs space-y-2">
                            <div>
                              <strong>What they want:</strong>
                              <div className="mt-1">{record.q6_wantFromJourney}</div>
                            </div>
                            {record.q7_liveWebinar === 'Yes' && record.q7_email !== 'Not provided' && (
                              <div>
                                <strong>Email:</strong>
                                <div className="mt-1">{record.q7_email}</div>
                              </div>
                            )}
                          </div>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Survey Pagination */}
            {totalSurveyPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {indexOfFirstSurveyRecord + 1} to {Math.min(indexOfLastSurveyRecord, surveyRecords.length)} of {surveyRecords.length} entries
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentSurveyPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentSurveyPage === 1}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    ← Previous
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: totalSurveyPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentSurveyPage(page)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          currentSurveyPage === page
                            ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentSurveyPage(prev => Math.min(prev + 1, totalSurveyPages))}
                    disabled={currentSurveyPage === totalSurveyPages}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Webinar Registrations */}
        {activeTab === 'webinar' && (
          <div className="mt-8">
            {/* Stats */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Storage Used</h3>
                  <span className="text-2xl">💾</span>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {formatBytes(webinarFileSize)}
                </p>
                <p className="text-sm text-gray-600">
                  Webinar registration data
                </p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium text-gray-600">Total Registrations</h3>
                  <span className="text-2xl">🎓</span>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-2">
                  {webinarRecordCount}
                </p>
                <p className="text-sm text-gray-600">
                  People registered for webinar
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4 mb-6">
              <button
                onClick={handleWebinarDownload}
                disabled={loading || webinarRecordCount === 0}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>⬇️</span>
                    <span>Download Webinar CSV</span>
                  </>
                )}
              </button>

              <button
                onClick={fetchWebinarInfo}
                disabled={loading}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50"
              >
                🔄 Refresh Data
              </button>
            </div>

            {/* Records Table */}
            {webinarRecordCount === 0 ? (
              <div className="bg-gray-50 rounded-xl p-12 text-center">
                <p className="text-2xl mb-2">📭</p>
                <p className="text-gray-600 text-lg font-medium mb-2">No registrations yet</p>
                <p className="text-gray-500 text-sm">Webinar registrations will appear here once users sign up.</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-green-50 to-blue-50">
                    <tr>
                      <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Phone</th>
                      <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">Registration Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentWebinarRecords.map((record, index) => (
                      <tr key={index} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">👤</span>
                            <span className="font-medium text-gray-900">{record.name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <a 
                            href={`mailto:${record.email}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {record.email}
                          </a>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-1">
                            <span>🇮🇳</span>
                            <span className="text-gray-700">+91 {record.phone}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600">
                          {formatDate(record.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Webinar Pagination */}
            {totalWebinarPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing {indexOfFirstWebinarRecord + 1} to {Math.min(indexOfLastWebinarRecord, webinarRecords.length)} of {webinarRecords.length} entries
                </p>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentWebinarPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentWebinarPage === 1}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    ← Previous
                  </button>
                  
                  <div className="flex gap-1">
                    {Array.from({ length: totalWebinarPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentWebinarPage(page)}
                        className={`px-4 py-2 rounded-lg transition-all ${
                          currentWebinarPage === page
                            ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentWebinarPage(prev => Math.min(prev + 1, totalWebinarPages))}
                    disabled={currentWebinarPage === totalWebinarPages}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
          <h3 className="font-semibold text-blue-900 mb-2">📌 Important Notes</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            {activeTab === 'magic' ? (
              <>
                <li>• CSV file contains: Email, Conclusion, Timestamp, and User Path</li>
                <li>• Data is stored securely in Vercel Blob Storage</li>
                <li>• Free tier provides 500 MB of storage</li>
                <li>• Download regularly to keep backups</li>
              </>
            ) : activeTab === 'survey' ? (
              <>
                <li>• CSV file contains all survey responses with timestamps</li>
                <li>• Data is stored securely in Vercel Blob Storage</li>
                <li>• Survey responses help understand community needs</li>
                <li>• Download regularly to analyze trends and insights</li>
              </>
            ) : (
              <>
                <li>• CSV file contains: Name, Email, Phone, and Timestamp</li>
                <li>• Data is stored securely in Vercel Blob Storage</li>
                <li>• All phone numbers are prefixed with +91 (India)</li>
                <li>• Download regularly to manage registrations and follow-ups</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminMagicData;
